/**
 * API Service - TypeScript
 * Portal ICAVI - Sprint 4 Migration
 * Serviço centralizado para requisições HTTP com interceptors e gerenciamento de tokens
 */
import axios from 'axios';
import { api, apiInstance, API_BASE_URL } from './apiClient';
import { connectivityGuard } from './connectivityGuard';
import { cidadesServiceMethods } from './domains/cidadesService';
import { setoresServiceMethods } from './domains/setoresService';
import { empresasServiceMethods } from './domains/empresasService';
import { restaurantesServiceMethods } from './domains/restaurantesService';
import { pontosServiceMethods } from './domains/pontosService';
import { perfisServiceMethods } from './domains/perfisService';
import { colaboradoresServiceMethods } from './domains/colaboradoresService';
import { horaExtraServiceMethods } from './domains/horaExtraService';
import { carrosServiceMethods } from './domains/carrosService';
import { aluguelCarrosServiceMethods } from './domains/aluguelCarrosService';
import { calendarioServiceMethods } from './domains/calendarioService';
import { lanchesServiceMethods } from './domains/lanchesService';
import { usuariosServiceMethods } from './domains/usuariosService';
import { aprovacaoProducaoServiceMethods } from './domains/aprovacaoProducaoService';
import { portariaServiceMethods } from './domains/portariaService';
import { recepcaoServiceMethods } from './domains/recepcaoService';
import { sistemaLogsServiceMethods } from './domains/sistemaLogsService';
import { sanitizeApiUrlForLog } from './sanitizeApiUrlForLog';
// ============================================================================
// Types & Interfaces
// ============================================================================
// ============================================================================
// API Service
// ============================================================================
// Infraestrutura HTTP (axios instance, interceptors) movida para ./apiClient.ts
// Domínios extraídos: Cidades, Setores, Empresas, Restaurantes, Perfis e Pontos → ./domains/
// Domínios extraídos: Colaboradores, HoraExtra, Carros, AluguelCarros e Calendario → ./domains/
// Domínios extraídos: Lanches, Usuarios, AprovacaoProducao e Portaria → ./domains/
// Domínios extraídos: Recepcao e Logs do Sistema → ./domains/
export const apiService = {
    // ==========================================
    // MÃ‰TODOS GENÃ‰RICOS HTTP
    // ==========================================
    /**
     * Realiza requisição GET genérica
     * @param url URL do endpoint
     * @param config Configurações adicionais da requisição
     * @returns Dados da resposta
     */
    async get(url, config = {}) {
        try {
            const response = await api.get(url, config);
            return response.data;
        }
        catch (error) {
            // Indisponibilidade de backend ou 401 de sessão invalidada já são tratados
            // e logados centralmente (connectivityGuard) — evita inundar o console.
            if (!connectivityGuard.isExpectedTransient(error)) {
                console.error(`Erro GET ${sanitizeApiUrlForLog(url)}:`, error);
            }
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Realiza requisições POST genérica
     * @param url URL do endpoint
     * @param data Dados a serem enviados
     * @param config Configurações adicionais da requisição
     * @returns Dados da resposta
     */
    async post(url, data = {}, config = {}) {
        try {
            const response = await api.post(url, data, config);
            return response.data;
        }
        catch (error) {
            if (!connectivityGuard.isExpectedTransient(error)) {
                console.error(`Erro POST ${sanitizeApiUrlForLog(url)}:`, error);
            }
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Realiza requisição PUT genérica
     * @param url URL do endpoint
     * @param data Dados a serem atualizados
     * @param config Configurações adicionais da requisição
     * @returns Dados da resposta
     */
    async put(url, data = {}, config = {}) {
        try {
            const response = await api.put(url, data, config);
            return response.data;
        }
        catch (error) {
            if (!connectivityGuard.isExpectedTransient(error)) {
                console.error(`Erro PUT ${sanitizeApiUrlForLog(url)}:`, error);
            }
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Realiza requisição PATCH genérica (atualização parcial)
     * @param url URL do endpoint
     * @param data Dados parciais a enviar
     * @param config Configurações adicionais da requisição
     * @returns Dados da resposta
     */
    async patch(url, data = {}, config = {}) {
        try {
            const response = await api.patch(url, data, config);
            return response.data;
        }
        catch (error) {
            if (!connectivityGuard.isExpectedTransient(error)) {
                console.error(`Erro PATCH ${sanitizeApiUrlForLog(url)}:`, error);
            }
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    /**
     * Realiza requisição DELETE genérica
     * @param url URL do endpoint
     * @param config Configurações adicionais da requisição
     * @returns Dados da resposta
     */
    async delete(url, config = {}) {
        try {
            const response = await api.delete(url, config);
            return response.data;
        }
        catch (error) {
            if (!connectivityGuard.isExpectedTransient(error)) {
                console.error(`Erro DELETE ${sanitizeApiUrlForLog(url)}:`, error);
            }
            if (axios.isAxiosError(error) && error.response?.data) {
                return error.response.data;
            }
            throw error;
        }
    },
    // ==========================================
    // CIDADES (extraído para ./domains/cidadesService.ts)
    // ==========================================
    ...cidadesServiceMethods,
    // ==========================================
    // COLABORADORES (extraído para ./domains/colaboradoresService.ts)
    // ==========================================
    ...colaboradoresServiceMethods,
    // ==========================================
    // PONTOS (extraído para ./domains/pontosService.ts)
    // ==========================================
    ...pontosServiceMethods,
    // ==========================================
    // SETORES (extraído para ./domains/setoresService.ts)
    // ==========================================
    ...setoresServiceMethods,
    // ==========================================
    // CALENDÁRIO DE EVENTOS (extraído para ./domains/calendarioService.ts)
    // ==========================================
    ...calendarioServiceMethods,
    // HORA EXTRA (extraído para ./domains/horaExtraService.ts)
    // ==========================================
    ...horaExtraServiceMethods,
    // ==========================================
    // RELATÓRIOS DE LANCHES (extraído para ./domains/lanchesService.ts)
    // ==========================================
    ...lanchesServiceMethods,
    // ==========================================
    // USUÁRIOS (extraído para ./domains/usuariosService.ts)
    // ==========================================
    ...usuariosServiceMethods,
    // ==========================================
    // APROVAÇÃO DA PRODUÇÃO (extraído para ./domains/aprovacaoProducaoService.ts)
    // ==========================================
    ...aprovacaoProducaoServiceMethods,
    // GESTÃO DE CARROS (extraído para ./domains/carrosService.ts)
    // ==========================================
    ...carrosServiceMethods,
    // ==========================================
    // PORTARIA - FECHAMENTO DE ALMOÇOS (extraído para ./domains/portariaService.ts)
    // ==========================================
    ...portariaServiceMethods,
    // ==========================================
    // RECEPÇÃO (extraído para ./domains/recepcaoService.ts)
    // ==========================================
    ...recepcaoServiceMethods,
    // ==========================================
    // SISTEMA - LOGS (extraído para ./domains/sistemaLogsService.ts)
    // ==========================================
    ...sistemaLogsServiceMethods,
    // ==========================================
    // PERFIS E PERMISSÃ•ES
    // ==========================================
    /**
     * Busca lista de perfis
     * @returns Lista de perfis
     */
    ...perfisServiceMethods,
    // ==========================================
    // EMPRESAS (extraído para ./domains/empresasService.ts)
    // ==========================================
    ...empresasServiceMethods,
    // ==========================================
    // RESTAURANTES (extraído para ./domains/restaurantesService.ts)
    // ==========================================
    ...restaurantesServiceMethods,
    // COMPRAS - ALUGUEL DE CARROS + SAC - RESERVAS DE CARROS
    // (extraído para ./domains/aluguelCarrosService.ts)
    // ==========================================
    ...aluguelCarrosServiceMethods,
    getBaseUrl() {
        if (API_BASE_URL) {
            return API_BASE_URL;
        }
        if (typeof window !== 'undefined') {
            if (window.location.port === '3001') {
                return 'http://localhost:5000';
            }
            return `${window.location.protocol}//${window.location.host}`;
        }
        return '';
    },
};
/**
 * ExportaÃ§Ã£o default para compatibilidade
 */
export default apiService;
// Re-exporta apiInstance preservando o contrato de importadores externos
// (alguns módulos importam diretamente de 'services/core/apiService')
export { apiInstance };
